import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Users, UserPlus, ShoppingCart, AlertCircle, Phone, Mail, Gift, BadgePercent } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PLAYER = {
    name: '',
    date_of_birth: '',
    mobile_number: '',
    email: '',
    affiliation_type: 'school',
    affiliation_name: ''
};

const TeamRegistrationDialog = ({ 
    open, 
    onOpenChange, 
    tournament, 
    eligibleAgeCategories,
    onSubmit,
    isSubmitting 
}) => {
    const [teamName, setTeamName] = useState('');
    const [selectedAgeCategory, setSelectedAgeCategory] = useState('');
    const [players, setPlayers] = useState(
        Array(8).fill(null).map(() => ({ ...EMPTY_PLAYER }))
    );
    const [coach, setCoach] = useState({ name: '', mobile_number: '', email: '' });
    const [errors, setErrors] = useState({});
    const [schools, setSchools] = useState([]);

    const minPlayers = 8;
    const maxPlayers = 15;

    // Fetch schools with waiver info when dialog opens
    useEffect(() => {
        if (open) {
            fetchSchools();
        }
    }, [open]);

    const fetchSchools = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/school-rankings`);
            setSchools(response.data || []);
        } catch (error) {
            console.error('Failed to fetch schools:', error);
        }
    };

    // Get waiver info for a specific school/academy name
    const getWaiverInfo = (affiliationType, affiliationName) => {
        if (!affiliationName || affiliationType === 'private') return null;
        const school = schools.find(s => 
            s.school_name?.toLowerCase() === affiliationName.toLowerCase()
        );
        if (!school || !school.fee_waiver_type || school.fee_waiver_type === 'none') return null;
        return {
            type: school.fee_waiver_type,
            amount: school.fee_waiver_amount || 0,
            reason: school.fee_waiver_reason || ''
        };
    };

    // Calculate the best waiver from all players (use the highest discount)
    const teamWaiver = useMemo(() => {
        let bestWaiver = null;
        let bestDiscount = 0;

        players.forEach(player => {
            const waiver = getWaiverInfo(player.affiliation_type, player.affiliation_name);
            if (waiver) {
                if (waiver.type === 'full') {
                    // Full waiver is always the best
                    bestWaiver = waiver;
                    bestDiscount = Infinity;
                } else if (waiver.type === 'partial' && waiver.amount > bestDiscount && bestDiscount !== Infinity) {
                    bestWaiver = waiver;
                    bestDiscount = waiver.amount;
                }
            }
        });

        return bestWaiver;
    }, [players, schools]);

    const addPlayer = () => {
        if (players.length < maxPlayers) {
            setPlayers([...players, { ...EMPTY_PLAYER }]);
        }
    };

    const removePlayer = (index) => {
        if (players.length > minPlayers) {
            setPlayers(players.filter((_, i) => i !== index));
        }
    };

    const updatePlayer = (index, field, value) => {
        const newPlayers = [...players];
        newPlayers[index] = { ...newPlayers[index], [field]: value };
        setPlayers(newPlayers);
        
        // Clear error for this field
        const newErrors = { ...errors };
        if (newErrors[`player_${index}_${field}`]) {
            delete newErrors[`player_${index}_${field}`];
        }
        
        // Real-time age validation when DOB is entered
        if (field === 'date_of_birth' && value && selectedAgeCategory) {
            const playerAge = calculateAge(value);
            const ageLimit = getCategoryAgeLimit(selectedAgeCategory);
            if (playerAge !== null && playerAge >= ageLimit) {
                newErrors[`player_${index}_dob`] = `Player is ${playerAge}yo - must be under ${ageLimit} for ${selectedAgeCategory}`;
            }
        }
        
        setErrors(newErrors);
    };

    // Calculate age from date of birth
    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Get age limit from category (e.g., "U17" returns 17)
    const getCategoryAgeLimit = (category) => {
        if (!category || category === 'Senior' || category === 'Open') return 100;
        const match = category.match(/U(\d+)/);
        return match ? parseInt(match[1]) : 100;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!teamName.trim()) {
            newErrors.teamName = 'Team name is required';
        }

        if (!selectedAgeCategory) {
            newErrors.ageCategory = 'Age category is required';
        }

        if (!coach.name.trim()) {
            newErrors.coachName = 'Coach/Manager name is required';
        }

        if (!coach.mobile_number.trim()) {
            newErrors.coachMobile = 'Coach/Manager mobile number is required';
        }

        // Get age limit for selected category
        const ageLimit = getCategoryAgeLimit(selectedAgeCategory);
        let hasAgeViolation = false;
        const ineligiblePlayers = [];

        players.forEach((player, index) => {
            if (!player.name.trim()) {
                newErrors[`player_${index}_name`] = 'Name required';
            }
            if (!player.date_of_birth) {
                newErrors[`player_${index}_dob`] = 'DOB required';
            } else if (selectedAgeCategory) {
                // Validate player age against selected category
                const playerAge = calculateAge(player.date_of_birth);
                if (playerAge !== null && playerAge >= ageLimit) {
                    newErrors[`player_${index}_dob`] = `Player is ${playerAge} years old - must be under ${ageLimit} for ${selectedAgeCategory}`;
                    hasAgeViolation = true;
                    ineligiblePlayers.push({
                        index: index + 1,
                        name: player.name || `Player ${index + 1}`,
                        age: playerAge
                    });
                }
            }
            // Mobile number validation
            if (!player.mobile_number || !player.mobile_number.trim()) {
                newErrors[`player_${index}_mobile`] = 'Mobile number required';
            }
            // Email validation
            if (!player.email || !player.email.trim()) {
                newErrors[`player_${index}_email`] = 'Email required';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(player.email.trim())) {
                newErrors[`player_${index}_email`] = 'Invalid email format';
            }
            if (player.affiliation_type !== 'private' && !player.affiliation_name.trim()) {
                newErrors[`player_${index}_affiliation`] = `${player.affiliation_type === 'school' ? 'School' : 'Academy'} name required`;
            }
        });

        // Show detailed age violation error
        if (hasAgeViolation) {
            const playerDetails = ineligiblePlayers.map(p => `${p.name} (${p.age} years)`).join(', ');
            toast.error(`Age violation: ${playerDetails} - All players must be under ${ageLimit} for ${selectedAgeCategory}`);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            toast.error('Please fill in all required fields');
            return;
        }

        const registrationData = {
            team_name: teamName.trim(),
            age_category: selectedAgeCategory,
            players: players.map(p => ({
                name: p.name.trim(),
                date_of_birth: p.date_of_birth,
                mobile_number: p.mobile_number.trim(),
                email: p.email.trim(),
                affiliation_type: p.affiliation_type,
                affiliation_name: p.affiliation_type === 'private' ? null : p.affiliation_name.trim()
            })),
            coach: {
                name: coach.name.trim(),
                mobile_number: coach.mobile_number.trim(),
                email: coach.email.trim() || null
            },
            disciplines: [],
            // Include waiver info if applicable
            applied_waiver: teamWaiver ? {
                type: teamWaiver.type,
                amount: teamWaiver.amount,
                reason: teamWaiver.reason
            } : null
        };

        onSubmit(registrationData);
    };

    const resetForm = () => {
        setTeamName('');
        setSelectedAgeCategory('');
        setPlayers(Array(8).fill(null).map(() => ({ ...EMPTY_PLAYER })));
        setCoach({ name: '', mobile_number: '', email: '' });
        setErrors({});
    };

    const handleOpenChange = (isOpen) => {
        if (!isOpen) {
            resetForm();
        }
        onOpenChange(isOpen);
    };

    // Calculate fee - use the same registration_fee as configured in create tournament
    const calculateFee = () => {
        const baseFee = tournament?.registration_fee || tournament?.entry_fee || 0;
        if (!teamWaiver) return baseFee;
        
        if (teamWaiver.type === 'full') return 0;
        if (teamWaiver.type === 'partial') {
            const discounted = baseFee - (teamWaiver.amount || 0);
            return discounted > 0 ? discounted : 0;
        }
        return baseFee;
    };

    const getCurrencySymbol = (currency) => {
        const symbols = { KES: 'KSh', USD: '$', EUR: '€', GBP: '£' };
        return symbols[currency] || (currency || 'KSh') + ' ';
    };

    // Get unique schools from the schools list for dropdown suggestions
    const schoolOptions = useMemo(() => {
        return schools.map(s => s.school_name).filter(Boolean).sort();
    }, [schools]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="bg-[#0d2847] border border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
                        <Users className="w-6 h-6 text-primary" />
                        Team Registration
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        Register your team for {tournament?.name}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Team Info Section */}
                    <div className="bg-[#0a1628] p-4 border border-white/5 rounded space-y-4">
                        <h3 className="font-heading font-bold uppercase tracking-tight text-sm flex items-center gap-2">
                            <Users className="w-4 h-4 text-primary" />
                            Team Information
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Team Name *</Label>
                                <Input
                                    value={teamName}
                                    onChange={(e) => {
                                        setTeamName(e.target.value);
                                        if (errors.teamName) {
                                            const newErrors = { ...errors };
                                            delete newErrors.teamName;
                                            setErrors(newErrors);
                                        }
                                    }}
                                    placeholder="Enter team name"
                                    className={`bg-[#0d2847] border-white/10 ${errors.teamName ? 'border-red-500' : ''}`}
                                    data-testid="team-name-input"
                                />
                                {errors.teamName && <p className="text-red-500 text-xs">{errors.teamName}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Age Category *</Label>
                                <select
                                    value={selectedAgeCategory}
                                    onChange={(e) => {
                                        const newCategory = e.target.value;
                                        setSelectedAgeCategory(newCategory);
                                        
                                        // Re-validate all players against new category
                                        const newErrors = { ...errors };
                                        delete newErrors.ageCategory;
                                        
                                        const ageLimit = getCategoryAgeLimit(newCategory);
                                        players.forEach((player, index) => {
                                            // Clear previous DOB errors first
                                            delete newErrors[`player_${index}_dob`];
                                            
                                            if (player.date_of_birth && newCategory) {
                                                const playerAge = calculateAge(player.date_of_birth);
                                                if (playerAge !== null && playerAge >= ageLimit) {
                                                    newErrors[`player_${index}_dob`] = `Player is ${playerAge}yo - must be under ${ageLimit} for ${newCategory}`;
                                                }
                                            }
                                        });
                                        
                                        setErrors(newErrors);
                                    }}
                                    className={`w-full h-10 px-3 bg-[#0d2847] border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-white ${errors.ageCategory ? 'border-red-500' : 'border-white/10'}`}
                                    data-testid="team-age-category-select"
                                >
                                    <option value="" disabled>Select age category</option>
                                    {eligibleAgeCategories.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                                {selectedAgeCategory && (
                                    <p className="text-xs text-primary">
                                        All players must be under {getCategoryAgeLimit(selectedAgeCategory)} years old
                                    </p>
                                )}
                                {errors.ageCategory && <p className="text-red-500 text-xs">{errors.ageCategory}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Coach/Manager Section */}
                    <div className="bg-[#0a1628] p-4 border border-white/5 rounded space-y-4">
                        <h3 className="font-heading font-bold uppercase tracking-tight text-sm">
                            Coach / Manager Details
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Name *</Label>
                                <Input
                                    value={coach.name}
                                    onChange={(e) => {
                                        setCoach({ ...coach, name: e.target.value });
                                        if (errors.coachName) {
                                            const newErrors = { ...errors };
                                            delete newErrors.coachName;
                                            setErrors(newErrors);
                                        }
                                    }}
                                    placeholder="Coach/Manager name"
                                    className={`bg-[#0d2847] border-white/10 ${errors.coachName ? 'border-red-500' : ''}`}
                                    data-testid="coach-name-input"
                                />
                                {errors.coachName && <p className="text-red-500 text-xs">{errors.coachName}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Mobile Number *</Label>
                                <Input
                                    value={coach.mobile_number}
                                    onChange={(e) => {
                                        setCoach({ ...coach, mobile_number: e.target.value });
                                        if (errors.coachMobile) {
                                            const newErrors = { ...errors };
                                            delete newErrors.coachMobile;
                                            setErrors(newErrors);
                                        }
                                    }}
                                    placeholder="+254 xxx xxx xxx"
                                    className={`bg-[#0d2847] border-white/10 ${errors.coachMobile ? 'border-red-500' : ''}`}
                                    data-testid="coach-mobile-input"
                                />
                                {errors.coachMobile && <p className="text-red-500 text-xs">{errors.coachMobile}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Email (Optional)</Label>
                                <Input
                                    value={coach.email}
                                    onChange={(e) => setCoach({ ...coach, email: e.target.value })}
                                    placeholder="coach@email.com"
                                    className="bg-[#0d2847] border-white/10"
                                    data-testid="coach-email-input"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Players Section */}
                    <div className="bg-[#0a1628] p-4 border border-white/5 rounded space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-heading font-bold uppercase tracking-tight text-sm flex items-center gap-2">
                                <UserPlus className="w-4 h-4 text-primary" />
                                Players ({players.length}/{maxPlayers})
                            </h3>
                            {players.length < maxPlayers && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={addPlayer}
                                    className="border-primary/50 text-primary hover:bg-primary/10"
                                    data-testid="add-player-btn"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Player
                                </Button>
                            )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <AlertCircle className="w-4 h-4" />
                            <span>Minimum {minPlayers} players, Maximum {maxPlayers} players required</span>
                        </div>

                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                            {players.map((player, index) => (
                                <div 
                                    key={index} 
                                    className="bg-[#0d2847] p-4 border border-white/10 rounded space-y-3"
                                    data-testid={`player-${index + 1}-card`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-primary">Player {index + 1}</span>
                                        {players.length > minPlayers && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removePlayer(index)}
                                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                                                data-testid={`remove-player-${index + 1}-btn`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Full Name *</Label>
                                            <Input
                                                value={player.name}
                                                onChange={(e) => updatePlayer(index, 'name', e.target.value)}
                                                placeholder="Player name"
                                                className={`bg-[#0a1628] border-white/10 h-9 text-sm ${errors[`player_${index}_name`] ? 'border-red-500' : ''}`}
                                                data-testid={`player-${index + 1}-name-input`}
                                            />
                                            {errors[`player_${index}_name`] && <p className="text-red-500 text-xs">{errors[`player_${index}_name`]}</p>}
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Date of Birth *</Label>
                                            <Input
                                                type="date"
                                                value={player.date_of_birth}
                                                onChange={(e) => updatePlayer(index, 'date_of_birth', e.target.value)}
                                                className={`bg-[#0a1628] border-white/10 h-9 text-sm ${errors[`player_${index}_dob`] ? 'border-red-500' : ''}`}
                                                data-testid={`player-${index + 1}-dob-input`}
                                            />
                                            {errors[`player_${index}_dob`] && <p className="text-red-500 text-xs">{errors[`player_${index}_dob`]}</p>}
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Affiliation *</Label>
                                            <select
                                                value={player.affiliation_type}
                                                onChange={(e) => updatePlayer(index, 'affiliation_type', e.target.value)}
                                                className="w-full h-9 px-2 bg-[#0a1628] border border-white/10 rounded-md text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                                data-testid={`player-${index + 1}-affiliation-select`}
                                            >
                                                <option value="school">School</option>
                                                <option value="academy">Sports Academy</option>
                                                <option value="private">Private/Independent</option>
                                            </select>
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">
                                                {player.affiliation_type === 'school' ? 'School Name *' : 
                                                 player.affiliation_type === 'academy' ? 'Academy Name *' : 
                                                 'Institution (Optional)'}
                                            </Label>
                                            <Input
                                                value={player.affiliation_name}
                                                onChange={(e) => updatePlayer(index, 'affiliation_name', e.target.value)}
                                                placeholder={player.affiliation_type === 'private' ? 'N/A' : `Enter ${player.affiliation_type} name`}
                                                disabled={player.affiliation_type === 'private'}
                                                list={`schools-list-${index}`}
                                                className={`bg-[#0a1628] border-white/10 h-9 text-sm ${errors[`player_${index}_affiliation`] ? 'border-red-500' : ''} ${player.affiliation_type === 'private' ? 'opacity-50' : ''}`}
                                                data-testid={`player-${index + 1}-affiliation-name-input`}
                                            />
                                            {/* Datalist for autocomplete */}
                                            <datalist id={`schools-list-${index}`}>
                                                {schoolOptions.map(name => (
                                                    <option key={name} value={name} />
                                                ))}
                                            </datalist>
                                            {errors[`player_${index}_affiliation`] && <p className="text-red-500 text-xs">{errors[`player_${index}_affiliation`]}</p>}
                                            {/* Show waiver badge if school has waiver */}
                                            {(() => {
                                                const waiver = getWaiverInfo(player.affiliation_type, player.affiliation_name);
                                                if (waiver) {
                                                    return (
                                                        <div className="flex items-center gap-1 mt-1" data-testid={`player-${index + 1}-waiver-badge`}>
                                                            {waiver.type === 'full' ? (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                                    <Gift className="w-3 h-3" />
                                                                    100% Fee Waiver
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                                                                    <BadgePercent className="w-3 h-3" />
                                                                    {getCurrencySymbol(tournament?.currency)}{waiver.amount} discount
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                    
                                    {/* Contact Information Row */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Phone className="w-3 h-3" />
                                                Mobile Number *
                                            </Label>
                                            <Input
                                                type="tel"
                                                value={player.mobile_number}
                                                onChange={(e) => updatePlayer(index, 'mobile_number', e.target.value)}
                                                placeholder="+254 7XX XXX XXX"
                                                className={`bg-[#0a1628] border-white/10 h-9 text-sm ${errors[`player_${index}_mobile`] ? 'border-red-500' : ''}`}
                                                data-testid={`player-${index + 1}-mobile-input`}
                                            />
                                            {errors[`player_${index}_mobile`] && <p className="text-red-500 text-xs">{errors[`player_${index}_mobile`]}</p>}
                                        </div>

                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Mail className="w-3 h-3" />
                                                Email Address *
                                            </Label>
                                            <Input
                                                type="email"
                                                value={player.email}
                                                onChange={(e) => updatePlayer(index, 'email', e.target.value)}
                                                placeholder="player@email.com"
                                                className={`bg-[#0a1628] border-white/10 h-9 text-sm ${errors[`player_${index}_email`] ? 'border-red-500' : ''}`}
                                                data-testid={`player-${index + 1}-email-input`}
                                            />
                                            {errors[`player_${index}_email`] && <p className="text-red-500 text-xs">{errors[`player_${index}_email`]}</p>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Fee Summary */}
                    <div className="bg-[#0a1628] p-4 border border-white/5 rounded space-y-3">
                        <h4 className="font-heading font-bold uppercase tracking-tight text-sm">
                            Registration Summary
                        </h4>
                        
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between text-muted-foreground">
                                <span>Team</span>
                                <span>{teamName || 'Not set'}</span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                                <span>Players</span>
                                <span>{players.length}</span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                                <span>Category</span>
                                <span>{selectedAgeCategory || 'Not selected'}</span>
                            </div>
                        </div>
                        
                        {/* Waiver Applied Notice */}
                        {teamWaiver && (
                            <div className="p-3 bg-green-900/30 border border-green-500/40 rounded" data-testid="waiver-applied-notice">
                                <div className="flex items-center gap-2">
                                    {teamWaiver.type === 'full' ? (
                                        <Gift className="w-5 h-5 text-green-400" />
                                    ) : (
                                        <BadgePercent className="w-5 h-5 text-green-400" />
                                    )}
                                    <div>
                                        <p className="text-sm font-semibold text-green-400">
                                            {teamWaiver.type === 'full' 
                                                ? 'Full Fee Waiver Applied!' 
                                                : `Partial Waiver Applied: ${getCurrencySymbol(tournament?.currency)}${teamWaiver.amount} discount`
                                            }
                                        </p>
                                        {teamWaiver.reason && (
                                            <p className="text-xs text-green-400/80">Reason: {teamWaiver.reason}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="border-t border-white/10 pt-3">
                            {teamWaiver && teamWaiver.type !== 'full' && (
                                <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                                    <span>Original Fee</span>
                                    <span className="line-through">
                                        {getCurrencySymbol(tournament?.currency)}
                                        {(tournament?.registration_fee || tournament?.entry_fee || 0).toLocaleString()}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">
                                    {teamWaiver ? 'Final Fee' : 'Total Fee'}
                                </span>
                                <span className={`font-mono text-xl ${teamWaiver?.type === 'full' ? 'text-green-400' : 'text-accent'}`} data-testid="team-registration-fee">
                                    {teamWaiver?.type === 'full' ? (
                                        'FREE'
                                    ) : (
                                        <>
                                            {getCurrencySymbol(tournament?.currency)}
                                            {calculateFee().toLocaleString()}
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            className="flex-1 border-white/10 hover:bg-white/5"
                            data-testid="cancel-team-registration-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="flex-1 bg-primary hover:bg-red-600 text-white"
                            data-testid="confirm-team-registration-btn"
                        >
                            {isSubmitting ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <ShoppingCart className="w-4 h-4 mr-2" />
                            )}
                            Add Team to Cart
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TeamRegistrationDialog;
